const EVENT_LABELS = {
  orderCreateCtrp: "Booking Created",
  orderUpdateCTripOrder: "Order Updated",
  assignRide: "Ride Assigned",
  rideAR: "Ride Accepted",
  amendBooking: "Booking Amended",
  updateBooking: "Booking Updated",
  startRide: "Ride Started",
  arrived: "Driver Arrived",
  rideInTransit: "Ride In Transit",
  rideEnd: "Ride Completed",
  abortBooking: "Booking Aborted",
  createRide: "Ride Created",
};

const DRIVER_EVENT_LABELS = {
  DRIVER_ARRIVED_AT_PICKUP: "Arrived at pickup",
  DRIVER_DEPARTED_TO_PICKUP: "Departed to pickup",
  DRIVER_SUBMITTED_CUSTOMER_NO_SHOW: "Customer no-show",
};

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;

  const startsLikeJson =
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    trimmed.startsWith('"');

  if (!startsLikeJson) return value;

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return value;
  }
};

export const normalizeValue = (value) => {
  const parsed = tryParseJson(value);
  if (parsed === value) return value;

  if (Array.isArray(parsed)) {
    return parsed.map((item) => normalizeValue(item));
  }

  if (parsed && typeof parsed === "object") {
    return Object.keys(parsed).reduce((acc, key) => {
      acc[key] = normalizeValue(parsed[key]);
      return acc;
    }, {});
  }

  return parsed;
};

export const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export const getLogTitle = (log) => {
  if (!log) return "Unknown Event";

  if (EVENT_LABELS[log.eventName]) return EVENT_LABELS[log.eventName];

  if (log.eventName) {
    return log.eventName
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^\w/, (char) => char.toUpperCase());
  }

  return "Unknown Event";
};

export const getActorLabel = (userId) => {
  if (!userId) return "Unknown";
  if (String(userId).toLowerCase() === "systemid") return "System";
  return userId;
};

export const humanizeKey = (key) => {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

export const getDriverEventLabel = (eventName) => {
  if (!eventName) return "Driver event";
  if (DRIVER_EVENT_LABELS[eventName]) return DRIVER_EVENT_LABELS[eventName];
  return humanizeKey(eventName);
};

export const getPlaceName = (place) => {
  if (place == null || place === "") return "—";
  if (typeof place === "object" && place.name) return place.name;
  const normalized = normalizeValue(place);
  if (normalized && typeof normalized === "object" && normalized.name) {
    return normalized.name;
  }
  if (typeof normalized === "string") return normalized;
  return "—";
};

export const parseCancellationReason = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object" && raw.reason) {
    return {
      reasonText: raw.reason,
      pictures: Array.isArray(raw.picture) ? raw.picture : [],
      location: raw.location || null,
    };
  }
  if (typeof raw !== "string") return null;
  const parsed = tryParseJson(raw);
  if (parsed && typeof parsed === "object" && parsed.reason) {
    return {
      reasonText: parsed.reason,
      pictures: Array.isArray(parsed.picture) ? parsed.picture : [],
      location: parsed.location || null,
    };
  }
  return { reasonText: raw, pictures: [], location: null };
};

export const extractCompanyFromLogs = (parsedLogs) => {
  if (!Array.isArray(parsedLogs)) return null;
  for (let i = parsedLogs.length - 1; i >= 0; i -= 1) {
    const log = parsedLogs[i];
    const after = log.normalizedAfterData;
    const before = log.normalizedBeforeData;
    const company = after?.company || before?.company;
    if (company && typeof company === "object") return company;
  }
  return null;
};

export const pickSafeCompanyFields = (company) => {
  if (!company || typeof company !== "object") return [];
  const rows = [
    ["companyName", "Company"],
    ["fullName", "Contact name"],
    ["displayName", "Display name"],
    ["email", "Email"],
    ["phoneNumber", "Phone"],
    ["companyAddress", "Address"],
    ["companyPostCode", "Post code"],
    ["companyCity", "City"],
    ["companyRegistrationNumber", "Registration no."],
    ["companyVATNumber", "VAT no."],
  ];
  return rows
    .map(([key, label]) => {
      const value = company[key];
      if (value == null || value === "") return null;
      return { label, value: String(value) };
    })
    .filter(Boolean);
};

export const getIntegrationCommissions = (bookingIntegration) => {
  if (!bookingIntegration || typeof bookingIntegration !== "object") return [];
  const list =
    bookingIntegration.Commissions ||
    bookingIntegration.commissions ||
    [];
  return Array.isArray(list) ? list : [];
};

export const commissionMatchesBookingCarType = (commission, carType) => {
  if (!carType || !commission?.serviceLevel) return false;
  return String(commission.serviceLevel) === String(carType);
};

export const formatCommissionTimeWindow = (fromTime, toTime) => {
  if (fromTime == null && toTime == null) return "—";
  if (fromTime && toTime) return `${fromTime} – ${toTime}`;
  return String(fromTime || toTime || "—");
};

export const prepareDriverEvents = (driverEvents) => {
  if (!Array.isArray(driverEvents)) return [];
  return driverEvents
    .filter((e) => e && !e.isDeleted)
    .map((e, index) => ({
      ...e,
      _key: e.id || `driver-event-${index}`,
      label: getDriverEventLabel(e.eventName),
    }))
    .sort(
      (a, b) =>
        new Date(a.createdAt || 0).getTime() -
        new Date(b.createdAt || 0).getTime()
    );
};

export const normalizeSearchDataList = (raw) => {
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [];
  return list.filter((item) => item != null && typeof item === "object");
};

export const formatSearchLocationText = (text) => {
  if (text == null || text === "") return "—";
  if (typeof text === "object" && text !== null) {
    if (text.name) return String(text.name);
    return JSON.stringify(text);
  }
  const normalized = normalizeValue(text);
  if (normalized && typeof normalized === "object" && normalized.name) {
    return String(normalized.name);
  }
  if (typeof normalized === "string") {
    return normalized.length > 160 ? `${normalized.slice(0, 157)}…` : normalized;
  }
  return String(text);
};

export const stringifyForDisplay = (value, space = 2) => {
  if (value == null) return "";
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed !== value) {
      return JSON.stringify(parsed, null, space);
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, space);
  } catch (e) {
    return String(value);
  }
};

export const parseLogsFromApiResponse = (apiResponse) => {
  if (!apiResponse || !apiResponse.bookingData) return [];
  const rawList = normalizeValue(apiResponse.bookingData);

  if (!Array.isArray(rawList)) return [];

  const sortedAscending = [...rawList].sort((a, b) => {
    const timeA = new Date(a.eventTime || 0).getTime();
    const timeB = new Date(b.eventTime || 0).getTime();
    return timeA - timeB;
  });

  return sortedAscending.map((item, index) => ({
    ...item,
    id: `${item.eventTime || "time"}-${item.eventName || "event"}-${index}`,
    title: getLogTitle(item),
    actorLabel: getActorLabel(item.userId),
    normalizedIncomingData: normalizeValue(item.incomingData),
    normalizedBeforeData: normalizeValue(item.dataBeforeChange),
    normalizedAfterData: normalizeValue(item.dataAfterChange),
  }));
};
